import { type GridPaginationModel } from '@mui/x-data-grid-pro';
import { z } from 'zod';

import { Common } from 'vivaa-api';

import type * as api from '../../api';
import { isDefined } from '../../utils/types';

export const ROWS_PER_PAGE_OPTIONS = [5, 25, 50, 100, 250, 500];

export const UNKNOWN_ROW_COUNT = -1;

export const RowsPerPageSchema = z.number().refine((val) => ROWS_PER_PAGE_OPTIONS.includes(val), {
  message: `pageSize must be one of ${ROWS_PER_PAGE_OPTIONS.join(', ')}`,
});
// Must match ROWS_PER_PAGE_OPTIONS
export type RowsPerPage = 5 | 25 | 50 | 100 | 250 | 500;

export function getEstimatedRowCount(
  data: unknown[] | undefined,
  paginationModel: GridPaginationModel
): number {
  const { pageSize, page } = paginationModel;
  return isDefined(data) && data.length < pageSize
    ? data.length + page * pageSize
    : UNKNOWN_ROW_COUNT;
}

export function getRowCountProps(options: {
  data: unknown[] | undefined;
  paginationModel: GridPaginationModel;
  paginationSummary: Common.Pagination.PaginationSummary | undefined;
}): { rowCount: number; estimatedRowCount: number | undefined } {
  const { data, paginationModel, paginationSummary } = options;

  const lastPageRowCount = getEstimatedRowCount(data, paginationModel);
  const serverRowCount =
    paginationSummary?.status === Common.Pagination.ListCountEstimateMode.Exact
      ? paginationSummary.total
      : UNKNOWN_ROW_COUNT;
  const rowCount = lastPageRowCount !== UNKNOWN_ROW_COUNT ? lastPageRowCount : serverRowCount;

  return {
    rowCount,
    estimatedRowCount: rowCount === UNKNOWN_ROW_COUNT ? paginationSummary?.total : undefined,
  };
}

export function formatPaginationDisplayedRows(options: {
  from: number;
  to: number;
  count: number;
  estimated?: number;
}): string {
  const { from, to, count, estimated: estimatedCount } = options;
  if (count !== UNKNOWN_ROW_COUNT) {
    return `${from}–${to} of ${count}`;
  }
  if (isDefined(estimatedCount)) {
    return `${from}–${to} of more than ${Math.max(estimatedCount, to)}`;
  }
  return `${from}–${to}`;
}

export function getCursorBasedEstimatedRowCount(
  paginatedResponse:
    Pick<api.Common.Pagination.PaginationResponse<unknown>, 'meta' | 'data'> | undefined,
  paginationModel: GridPaginationModel
): number {
  const { pageSize, page } = paginationModel;
  return paginatedResponse?.meta.page.after === null
    ? paginatedResponse.data.length + page * pageSize
    : UNKNOWN_ROW_COUNT;
}
